import { Component, ViewChild, AfterViewInit } from '@angular/core';
import { SharedService } from '../../shared';
import { ToastrService } from 'ngx-toastr';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { OptimizeService } from '../../shared/optimize.service';
import { SharedAuthService } from '../../shared/shared.auth.service';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { DailogModalComponent } from '../../shared/elements/dailog-modal/dailog-modal.component';
import { take } from 'rxjs/operators';

interface CacheKeyDetail {
  key: string;
  size: number | string;           // allow string like "1.2 MB"
  ttl: number | null;
  lastModified: string | null;     // parsed to Date when sorting
}

@Component({
  selector: 'app-cache-manager',
  templateUrl: './cache-manager.component.html',
  styleUrl: './cache-manager.component.css',
  standalone: false
})
export class CacheManagerComponent implements AfterViewInit {
  cacheKeys: CacheKeyDetail[] = [];
  selectedKeys: { [key: string]: boolean } = {};
  selectAll: boolean = false;
  loading = true;

  displayedColumns: string[] = ['key', 'size', 'lastModified'];
  dataSource = new MatTableDataSource<CacheKeyDetail>([]);
  cacheManagerPageAccess: boolean = false;

  // Use a setter so it runs when the table actually appears in the DOM
  private _sort?: MatSort;
  @ViewChild(MatSort) set matSort(sort: MatSort | undefined) {
    this._sort = sort;
    if (this.dataSource && this._sort) {
      this.dataSource.sort = this._sort;
    }
  }

  constructor(private sharedService: SharedService, private optimize: OptimizeService,
    public toaster: ToastrService,private sharedAuthService: SharedAuthService, public dialog: MatDialog) { }

  ngOnInit() {
    this.setupSorting();  // define sorting accessors once
    this.loadCacheKeys();
    if (!sessionStorage.getItem('techaccessData')) {
      this.sharedAuthService.userAccessDataSource.pipe(take(1)).subscribe(accessData => {
        this.checkForUserAccess();
      });
    } else {
      this.checkForUserAccess();
    }
  }

  ngAfterViewInit() {
    // In case the table is already there by now
    if (this._sort) {
      this.dataSource.sort = this._sort;
    }
  }

  checkForUserAccess() {
     this.cacheManagerPageAccess  = this.sharedAuthService.getTohPageAccessDetails('CacheManagement','CacheManager','View') || this.sharedAuthService.getTohPageAccessDetails('CacheManagement','CacheManager','Edit');
  }

  private setupSorting(): void {
    // Make size/date sort numerically/chronologically
    this.dataSource.sortingDataAccessor = (item: CacheKeyDetail, property: string) => {
      switch (property) {
        case 'size':
          return this.toBytes(item.size);
        case 'lastModified':
          return item.lastModified ? new Date(item.lastModified).getTime() : Number.NEGATIVE_INFINITY;
        default: {
          const value = (item as any)[property];
          return typeof value === 'string' ? value.toLowerCase() : value;
        }
      }
    };
  }

  private toBytes(size: number | string | null | undefined): number {
    if (size == null) return 0;
    if (typeof size === 'number') return size;

    const str = String(size).trim().toUpperCase();
    const m = str.match(/^([\d.]+)\s*(B|KB|MB|GB|TB)?$/);
    if (!m) {
      const n = parseFloat(str);
      return isNaN(n) ? 0 : n;
    }
    const val = parseFloat(m[1]);
    const unit = (m[2] || 'B') as 'B' | 'KB' | 'MB' | 'GB' | 'TB';
    const powMap = { B: 0, KB: 1, MB: 2, GB: 3, TB: 4 };
    return val * Math.pow(1024, powMap[unit]);
  }

  loadCacheKeys(): void {
    this.loading = true;
    this.sharedService.getAllCacheKeys().subscribe({
      next: response => {
        this.cacheKeys = response?.keys ?? [];
        this.dataSource.data = [...this.cacheKeys];

        // ensure checkboxes are reset
        this.selectedKeys = {};
        this.cacheKeys.forEach(item => (this.selectedKeys[item.key] = false));

        // If matSort was not available earlier due to *ngIf, bind it now
        queueMicrotask(() => {
          if (this._sort) this.dataSource.sort = this._sort;
        });

        this.loading = false;
      },
      error: err => {
        this.cacheKeys = [];
        this.dataSource.data = [];
        console.error('Failed to fetch cache keys:', err);
        this.loading = false;
      }
    });
  }

  updateCache(): void {
    const keysToUpdate = Object.keys(this.selectedKeys).filter(key => this.selectedKeys[key]);

    if (keysToUpdate.length === 0) {
      this.toaster.error('Please select at least one cache key to update.');
      return;
    }

    this.loading = true;

    this.sharedService.updateSelectedCache(keysToUpdate).subscribe({
      next: () => {
        this.toaster.success('Selected cache keys updated successfully!');
        this.loadCacheKeys(); // Refresh keys
        this.loading = false;

        // Unselect all checkboxes
        this.selectAll = false;
        this.cacheKeys.forEach(item => {
          this.selectedKeys[item.key] = false;
        });
      },
      error: err => {
        this.loading = false;
        console.error('Failed to update cache:', err);
        this.toaster.error('Failed to update cache.');
      }
    });
  }

  toggleSelectAll(): void {
    this.cacheKeys.forEach(item => {
      this.selectedKeys[item.key] = this.selectAll;
    });
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value.trim().toLowerCase();
    // Set once is enough, but safe here too
    this.dataSource.filterPredicate = (data, filter) =>
      data.key?.toLowerCase().includes(filter);
    this.dataSource.filter = filterValue;
  }


  deleteSelectedCache(): void {
    const keysToDelete = Object.keys(this.selectedKeys).filter(key => this.selectedKeys[key]);

    if (keysToDelete.length === 0) {
      this.toaster.error('Please select at least one cache key to delete.');
      return;
    }

    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '400px';
    dialogConfig.hasBackdrop = true;
    dialogConfig.minHeight = '200px';
    dialogConfig.height = 'auto';
    dialogConfig.autoFocus = false;
    dialogConfig.data = {
      title: 'Delete Confirmation!',
      subTitle: 'Are you sure to delete the selected cache key(s)?',
      buttonName: 'Delete'
    };

    const dialogRef = this.dialog.open(DailogModalComponent, dialogConfig);

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.data == 'save') {
        this.loading = true;
        this.sharedService.deleteSelectedCache(keysToDelete).subscribe({
          next: () => {
            this.toaster.success('Selected cache keys deleted successfully!');
            this.loadCacheKeys();
            this.loading = false;
            this.selectAll = false;
            this.cacheKeys.forEach(item => (this.selectedKeys[item.key] = false));
          },
          error: err => {
            this.loading = false;
            console.error('Failed to delete cache:', err);
            this.toaster.error('Failed to delete cache.');
          }
        });
      }
      else {
        this.loading = false;
        // console.log('deletion process is cancelled..........');
      }
    });

  }
}
